import Dexie, { Table } from 'dexie'

// Define entity interfaces for offline storage
export interface OfflineTimeEntry {
  id?: number // Auto-incremented local ID
  serverId?: string // ID from server once synced
  description: string
  projectId: string
  taskId?: string
  startTime: string
  endTime: string
  durationSeconds: number
  isBillable: boolean
  tags?: string[]
  status: 'draft' | 'synced' | 'error'
  createdAt: number
  updatedAt: number
  syncError?: string
}

export interface OfflineTimer {
  id: string // Local timer ID
  serverTimerId?: string
  projectId: string
  taskId?: string
  description: string
  status: 'running' | 'paused' | 'stopped'
  startedAt: number
  pausedAt?: number
  totalPausedSeconds: number
  elapsedSeconds: number
  createdAt: number
  updatedAt: number
}

export interface OfflineQueueItem {
  id?: number
  type: 'timer:start' | 'timer:pause' | 'timer:resume' | 'timer:stop' | 'timer:cancel' | 'entry:create' | 'entry:update' | 'entry:delete'
  payload: Record<string, unknown>
  status: 'pending' | 'processing' | 'completed' | 'failed'
  retryCount: number
  createdAt: number
  processedAt?: number
  error?: string
}

export interface PendingMutation {
  id?: number
  entityType: 'timeEntry' | 'timer' | 'project' | 'task'
  entityId: string
  operation: 'create' | 'update' | 'delete'
  data: Record<string, unknown>
  timestamp: number
  synced: boolean
  retryCount: number
  error?: string
}

class OfflineDatabase extends Dexie {
  timeEntries!: Table<OfflineTimeEntry, number>
  timers!: Table<OfflineTimer, string>
  queue!: Table<OfflineQueueItem, number>
  pendingMutations!: Table<PendingMutation, number>

  constructor() {
    super('TimeTrackerOfflineDB')
    
    this.version(1).stores({
      timeEntries: '++id, serverId, projectId, status, createdAt',
      timers: 'id, projectId, status, updatedAt',
      queue: '++id, type, status, createdAt',
      pendingMutations: '++id, entityType, entityId, synced, timestamp',
    })
  }

  // Time Entry operations
  async saveTimeEntry(entry: Omit<OfflineTimeEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<number> {
    const now = Date.now()
    return await this.timeEntries.add({
      ...entry,
      createdAt: now,
      updatedAt: now,
    })
  }

  async updateTimeEntry(id: number, updates: Partial<OfflineTimeEntry>): Promise<void> {
    await this.timeEntries.update(id, {
      ...updates,
      updatedAt: Date.now(),
    })
  }

  async getPendingTimeEntries(): Promise<OfflineTimeEntry[]> {
    return await this.timeEntries
      .where('status')
      .equals('draft')
      .toArray()
  }

  async markTimeEntrySynced(localId: number, serverId: string): Promise<void> {
    await this.timeEntries.update(localId, {
      serverId,
      status: 'synced',
      updatedAt: Date.now(),
    })
  }

  // Timer operations
  async saveTimer(timer: Omit<OfflineTimer, 'createdAt' | 'updatedAt'>): Promise<void> {
    const now = Date.now()
    await this.timers.put({
      ...timer,
      createdAt: now,
      updatedAt: now,
    })
  }

  async getActiveTimer(): Promise<OfflineTimer | undefined> {
    return await this.timers
      .where('status')
      .anyOf(['running', 'paused'])
      .first()
  }

  async deleteTimer(id: string): Promise<void> {
    await this.timers.delete(id)
  }

  // Queue operations
  async addToQueue(item: Omit<OfflineQueueItem, 'id' | 'createdAt' | 'retryCount' | 'status'>): Promise<number> {
    return await this.queue.add({
      ...item,
      status: 'pending',
      retryCount: 0,
      createdAt: Date.now(),
    })
  }

  async getPendingQueueItems(): Promise<OfflineQueueItem[]> {
    return await this.queue
      .where('status')
      .equals('pending')
      .sortBy('createdAt')
  }

  async markQueueItemProcessing(id: number): Promise<void> {
    await this.queue.update(id, {
      status: 'processing',
    })
  }

  async markQueueItemCompleted(id: number): Promise<void> {
    await this.queue.update(id, {
      status: 'completed',
      processedAt: Date.now(),
    })
  }

  async markQueueItemFailed(id: number, error: string): Promise<void> {
    const item = await this.queue.get(id)
    if (item) {
      await this.queue.update(id, {
        status: 'failed',
        error,
        retryCount: item.retryCount + 1,
        processedAt: Date.now(),
      })
    }
  }

  async clearCompletedQueueItems(): Promise<void> {
    await this.queue
      .where('status')
      .equals('completed')
      .delete()
  }

  // Pending mutations operations
  async addPendingMutation(mutation: Omit<PendingMutation, 'id' | 'timestamp' | 'synced' | 'retryCount'>): Promise<number> {
    return await this.pendingMutations.add({
      ...mutation,
      timestamp: Date.now(),
      synced: false,
      retryCount: 0,
    })
  }

  async getUnsyncedMutations(): Promise<PendingMutation[]> {
    return await this.pendingMutations
      .where('synced')
      .equals(0) // Dexie converts boolean to 0/1
      .sortBy('timestamp')
  }

  async markMutationSynced(id: number): Promise<void> {
    await this.pendingMutations.update(id, {
      synced: true,
    })
  }

  // Utility
  async clearAllData(): Promise<void> {
    await Promise.all([
      this.timeEntries.clear(),
      this.timers.clear(),
      this.queue.clear(),
      this.pendingMutations.clear(),
    ])
  }

  async getStorageSize(): Promise<number> {
    const entries = await this.timeEntries.count()
    const timers = await this.timers.count()
    const queue = await this.queue.count()
    const mutations = await this.pendingMutations.count()
    
    // Rough estimate: ~500 bytes per record average
    return (entries + timers + queue + mutations) * 500
  }
}

export const offlineDatabase = new OfflineDatabase()

export default offlineDatabase
